import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';

const DepartmentPositionScopePicker = ({
  selectedDepartments = [],
  selectedPositions = [],
  onDepartmentsChange,
  onPositionsChange,
  disabled = false,
  onLoadError,
}) => {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [departmentPickerSearch, setDepartmentPickerSearch] = useState('');
  const [positionPickerSearch, setPositionPickerSearch] = useState('');
  const departmentsFetchedRef = useRef(false);
  const positionsFetchedRef = useRef(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (departmentsFetchedRef.current) return;
      try {
        setDepartmentsLoading(true);
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/system/departments`,
          { headers },
        );
        const departments = Array.isArray(response.data?.departments)
          ? response.data.departments
          : [];
        setDepartmentOptions(departments);
        departmentsFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching departments:', err);
        onLoadError?.('Failed to load departments. Please try again.');
      } finally {
        setDepartmentsLoading(false);
      }
    };

    const fetchPositions = async () => {
      if (positionsFetchedRef.current) return;
      try {
        setPositionsLoading(true);
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/system/positions`,
          { headers },
        );
        const positions = Array.isArray(response.data?.positions)
          ? response.data.positions
          : [];
        setPositionOptions(positions);
        positionsFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching positions:', err);
        onLoadError?.('Failed to load positions. Please try again.');
      } finally {
        setPositionsLoading(false);
      }
    };

    fetchDepartments();
    fetchPositions();
  }, []);

  const filteredDepartmentsForPicker = useMemo(() => {
    const q = departmentPickerSearch.trim().toLowerCase();
    if (!q) return departmentOptions;
    return departmentOptions.filter((d) => String(d).toLowerCase().includes(q));
  }, [departmentOptions, departmentPickerSearch]);

  const filteredPositionsForPicker = useMemo(() => {
    const q = positionPickerSearch.trim().toLowerCase();
    if (!q) return positionOptions;
    return positionOptions.filter((p) => String(p).toLowerCase().includes(q));
  }, [positionOptions, positionPickerSearch]);

  const toggleDepartment = (dept) => {
    const next = selectedDepartments.includes(dept)
      ? selectedDepartments.filter((d) => d !== dept)
      : [...selectedDepartments, dept];
    onDepartmentsChange(next);
  };

  const togglePosition = (pos) => {
    const next = selectedPositions.includes(pos)
      ? selectedPositions.filter((p) => p !== pos)
      : [...selectedPositions, pos];
    onPositionsChange(next);
  };

  return (
    <>
      <div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-2">
          <label className="block text-sm font-medium text-gray-700 min-w-0 flex-1">
            Departments
            <span className="ml-2 text-xs font-normal text-gray-500">
              (leave empty to target all departments)
            </span>
          </label>
          <input
            type="search"
            placeholder="Search departments..."
            value={departmentPickerSearch}
            onChange={(e) => setDepartmentPickerSearch(e.target.value)}
            disabled={disabled || departmentsLoading || departmentOptions.length === 0}
            className="w-full sm:w-56 lg:w-64 shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            aria-label="Filter departments list"
          />
        </div>
        {departmentsLoading ? (
          <p className="text-sm text-gray-500">Loading departments...</p>
        ) : departmentOptions.length === 0 ? (
          <p className="text-sm text-gray-500">No departments found.</p>
        ) : filteredDepartmentsForPicker.length === 0 ? (
          <p className="text-sm text-gray-500">No departments match your search.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {filteredDepartmentsForPicker.map((dept) => (
              <label key={dept} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedDepartments.includes(dept)}
                  onChange={() => toggleDepartment(dept)}
                  disabled={disabled}
                  className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936] disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">{dept}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-2">
          <label className="block text-sm font-medium text-gray-700 min-w-0 flex-1">
            Positions
            <span className="ml-2 text-xs font-normal text-gray-500">
              (leave empty to target all positions)
            </span>
          </label>
          <input
            type="search"
            placeholder="Search positions..."
            value={positionPickerSearch}
            onChange={(e) => setPositionPickerSearch(e.target.value)}
            disabled={disabled || positionsLoading || positionOptions.length === 0}
            className="w-full sm:w-56 lg:w-64 shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            aria-label="Filter positions list"
          />
        </div>
        {positionsLoading ? (
          <p className="text-sm text-gray-500">Loading positions...</p>
        ) : positionOptions.length === 0 ? (
          <p className="text-sm text-gray-500">No positions found.</p>
        ) : filteredPositionsForPicker.length === 0 ? (
          <p className="text-sm text-gray-500">No positions match your search.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {filteredPositionsForPicker.map((pos) => (
              <label key={pos} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedPositions.includes(pos)}
                  onChange={() => togglePosition(pos)}
                  disabled={disabled}
                  className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936] disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">{pos}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DepartmentPositionScopePicker;
